import * as _ from 'lodash';
// tslint:disable-next-line:no-submodule-imports
import { all, call, Effect, put, putResolve, select } from 'redux-saga/effects';
import { User, UserCollection, Workspace2 } from '../../clients/aasServerClient.generated';
import * as UserRepository from '../../repositories/UserRepository';
import { fetchAllData, getSelfInfo } from '../auth/actionCreators';
import { unselectAll} from '../tables/actionCreators';
import { currentUser } from '../auth/selectors';
import { takeEveryWithErrorHandling } from '../sagas';
import { storePagingState } from '../tables/actionCreators';
import { getFilters, getLimit, getPaging, getSearch, getSorting, getStart } from '../tables/selectors';
import { TableFilters, TablePaging, TableSorting } from '../tables/state';
import {
    fetchAllUsers as fetchAllUsersCreator,
    fetchTableUsers as fetchUsersCreator,
    
    savedSingleUser,
    storeAllUsers,
    storeSingleUser,
    storeTableUsers,
    storeUserApplicationKey
} from './actionCreators';
import { fetchTableApplications } from '../applications/actionCreators';
import {
    ActionTypeNames, ActivateUsersAction, AssignUsersApplicationsAction,
    AssignUsersGroupsAction, AssignUsersWorkspaceAction, CopySingleUserAction, CreateSingleUserAction,
    DeactivateUsersAction, DeleteUsersAction, FetchSingleUserAction,
    ImportCSVAction,
    NewSingleUserAction,
    SaveSingleUserAction,
    SendMigrateAccountMailAction,
    SendMigratePasswordMailAction,
    SendNewUserMailAction,
    SendResetPasswordMailAction,
    SendVerifyEmailMailAction,
    SynchronizeUsersAction,
    CreateUsersApplicationKeyAction,
    UpdateUsersAction,
} from './actions';

function* FetchTableUsersNoSearch() {
    const start: number = yield select(getStart, 'users');
    const limit: number = yield select(getLimit, 'users');
    const sorting: TableSorting = yield select(getSorting, 'users');
    const paging: TablePaging = yield select(getPaging, 'users');
    const res: UserCollection = yield call(UserRepository.getUsersWoFilters, limit, start, sorting);
    yield all([
        putResolve(storeTableUsers(res.items)),
        putResolve(storePagingState('users', {
            numberOfItems: res.count || 0,
            numberOfPages: Math.ceil((res.count || 0) / paging.itemsPerPage)
        }))
    ]);
}

function* fetchTableUsers() {
    const start: number = yield select(getStart, 'users');
    const limit: number = yield select(getLimit, 'users');
    const sorting: TableSorting = yield select(getSorting, 'users');
    const  filters: TableFilters = yield select(getFilters, 'users'); ;
   

    const paging: TablePaging = yield select(getPaging, 'users');
    const search: string = yield select(getSearch, 'users');

    const res: UserCollection = yield call(UserRepository.getUsers, limit, start, sorting, filters, search);
    yield all([
        putResolve(storeTableUsers(res.items)),
        putResolve(storePagingState('users', {
            numberOfItems: res.count || 0,
            numberOfPages: Math.ceil((res.count || 0) / paging.itemsPerPage)
        }))
    ]);
}

export function* watchFetchTableUsersNoSearch() {
    yield takeEveryWithErrorHandling(ActionTypeNames.FetchTableUsersNoSearch, FetchTableUsersNoSearch);
}

export function* watchFetchTableUsers() {
    yield takeEveryWithErrorHandling(ActionTypeNames.FetchTableUsers, fetchTableUsers);
}

function* fetchAllUsers() {
    const res: UserCollection = yield call(UserRepository.getAllUsers);
    yield putResolve(storeAllUsers(res.items));
}

export function* watchFetchAllUsers() {
    yield takeEveryWithErrorHandling(ActionTypeNames.FetchAllUsers, fetchAllUsers);
}

function* fetchSingleUser(action: FetchSingleUserAction) {
    const user: User = yield call(UserRepository.getUser, action.id);
    yield putResolve(storeSingleUser(user));
}

function* watchFetchSingleUser() {
    yield takeEveryWithErrorHandling(ActionTypeNames.FetchSingleUser, fetchSingleUser);
}

function* copySingleUser(action: CopySingleUserAction) {
    const res: User = yield call(UserRepository.getUser, action.id);
    const user: Partial<User> = {
        active: true,
        ad_identifier: '',
        applications: res.applications,
        email: '',
        first_name: '',
        groups: res.groups,
        last_name: '',
        privileges: res.privileges,
        workspace: res.workspace,
    };
    yield putResolve(storeSingleUser(user));
}

function* watchCopySingleUser() {
    yield takeEveryWithErrorHandling(ActionTypeNames.CopySingleUser, copySingleUser);
}

function* newSingleUser(action: NewSingleUserAction) {
    const user: Partial<User> = {
        active: true,
        ad_identifier: '',
        applications: [],
        email: '',
        first_name: '',
        groups: [],
        last_name: '',
        privileges: [],
        workspace: new Workspace2({ id: '' }),
    };
    yield putResolve(storeSingleUser(user));
}

function* watchNewSingleUser() {
    yield takeEveryWithErrorHandling(ActionTypeNames.NewSingleUser, newSingleUser);
}

function* saveSingleUser(action: SaveSingleUserAction) {
    const res = yield call(
        UserRepository.updateUser,
        action.user,
        action.workspaceId,
        action.apps,
        action.groups,
        action.privileges
    );
    yield putResolve(savedSingleUser());
    yield all([
        put(fetchUsersCreator()),
        put(fetchAllUsersCreator()),
    ]);

    // If own user is edited, reload its information
    const ownUser: User | undefined = yield (select(currentUser));
    if (action.user.id === ownUser?.id) {
        yield put(getSelfInfo());
    }
}

function* watchSaveSingleUser() {
    yield takeEveryWithErrorHandling(ActionTypeNames.SaveSingleUser, saveSingleUser);
}

function* createSingleUser(action: CreateSingleUserAction) {
    const res = yield call(
        UserRepository.createUser,
        action.mail,
        action.user,
        action.workspaceId,
        action.apps,
        action.groups,
        action.privileges
    );
    yield putResolve(savedSingleUser());
    yield all([
        put(fetchUsersCreator()),
        put(fetchAllUsersCreator()),
    ]);
}

function* watchCreateSingleUser() {
    yield takeEveryWithErrorHandling(ActionTypeNames.CreateSingleUser, createSingleUser);
}

function* watchActivateUsers() {
    yield takeEveryWithErrorHandling(ActionTypeNames.ActivateUsers, activateUsers);
}

function* activateUsers(action: ActivateUsersAction) {
    yield call(UserRepository.activateUsers, action.userIds);
    yield putResolve(fetchUsersCreator());
}

function* watchUpdateUsers() {
    yield takeEveryWithErrorHandling(ActionTypeNames.UpdateUsers, updateUsers);
}

function* updateUsers(action: UpdateUsersAction) {
    yield call(UserRepository.updateUsers, action.userIds);
    yield putResolve(fetchUsersCreator());
}

function* watchDeactivateUsers() {
    yield takeEveryWithErrorHandling(ActionTypeNames.DeactivateUsers, deactivateUsers);
}

function* deactivateUsers(action: DeactivateUsersAction) {
    yield call(UserRepository.deactivateUsers, action.userIds);
    yield putResolve(fetchUsersCreator());
}

function* watchDeleteUsers() {
    yield takeEveryWithErrorHandling(ActionTypeNames.DeleteUsers, deleteUsers);
}

function* deleteUsers(action: DeleteUsersAction) {
    
    yield call(UserRepository.deleteUsers, action.userIds);
    yield put(unselectAll('users',action.userIds)) ;        
    yield put(fetchAllData());
    yield putResolve(fetchUsersCreator());   
}

function* watchSendNewUserMail() {
    yield takeEveryWithErrorHandling(ActionTypeNames.SendNewUserMail, sendNewUserMail);
}

function* sendNewUserMail(action: SendNewUserMailAction) {
    yield call(UserRepository.sendNewUserMail, action.userIds);
    yield putResolve(fetchUsersCreator());
}

function* watchSendResetPasswordMail() {
    yield takeEveryWithErrorHandling(ActionTypeNames.SendResetPasswordMail, sendResetPasswordMail);
}

function* sendResetPasswordMail(action: SendResetPasswordMailAction) {
    yield call(UserRepository.sendResetPasswordMail, action.userIds);
    yield putResolve(fetchUsersCreator());
}

function* watchSendVerifyEmailMail() {
    yield takeEveryWithErrorHandling(ActionTypeNames.SendVerifyEmailMail, sendVerifyEmailMail)
}

function* sendVerifyEmailMail(action: SendVerifyEmailMailAction) {
    yield call(UserRepository.sendVerifyEmailMail, action.userIds);
    yield putResolve(fetchUsersCreator());
}

function* watchSendMigratePasswordMail() {
    yield takeEveryWithErrorHandling(ActionTypeNames.SendMigratePasswordMail, sendMigratePasswordMail);
}

function* sendMigratePasswordMail(action: SendMigratePasswordMailAction) {
    yield call(UserRepository.sendMigratePasswordMail, action.userIds);
    yield putResolve(fetchUsersCreator());
}

function* watchSendMigrateAccountMail() {
    yield takeEveryWithErrorHandling(ActionTypeNames.SendMigrateAccountMail, sendMigrateAccountMail);
}

function* sendMigrateAccountMail(action: SendMigrateAccountMailAction) {
    yield call(UserRepository.sendMigrateAccountMail, action.userIds);
    yield putResolve(fetchUsersCreator());
}

function* watchSynchronizeUsers() {
    yield takeEveryWithErrorHandling(ActionTypeNames.SynchronizeUsers, synchronizeUsers);
}

function* synchronizeUsers(action: SynchronizeUsersAction) {
    yield call(UserRepository.synchronize, action.userIds);
    yield putResolve(fetchUsersCreator());
}

function* watchAssignUsersWorkspace() {
    yield takeEveryWithErrorHandling(ActionTypeNames.AssignUsersWorkspace, assignUsersWorkspace);
}

function* assignUsersWorkspace(action: AssignUsersWorkspaceAction) {
    yield call(UserRepository.assignUsersWorkspace, action.userIds, action.workspaceId);
    yield putResolve(fetchUsersCreator());
}

function* watchAssignUsersApplications() {
    yield takeEveryWithErrorHandling(ActionTypeNames.AssignUsersApplications, assignUsersApplications);
}

function* assignUsersApplications(action: AssignUsersApplicationsAction) {
    yield call(UserRepository.assignUsersApplications, action.userIds, action.appIds);
    yield putResolve(fetchUsersCreator());
    yield putResolve(fetchTableApplications());

    // If own user is edited, reload its information
    const ownUser: User | undefined = yield (select(currentUser));
    if (action.userIds.find((id) => id === ownUser?.id) !== undefined) {
        yield put(getSelfInfo());
    }
}

function* watchCreateApplicationKey() {
    yield takeEveryWithErrorHandling(ActionTypeNames.CreateUserApplicationKey, createApplicationKey);
}

function* createApplicationKey(action: CreateUsersApplicationKeyAction) {
    const res: string = yield call(UserRepository.createApplicationKey, action.userIds, action.appId);
    yield putResolve(storeUserApplicationKey(action.userIds, action.appId, res));
}

function* watchAssignUsersGroups() {
    yield takeEveryWithErrorHandling(ActionTypeNames.AssignUsersGroups, assignUsersGroups);
}

function* assignUsersGroups(action: AssignUsersGroupsAction) {
    yield call(UserRepository.assignUsersGroups, action.userIds, action.groupIds);
    yield putResolve(fetchUsersCreator());

    // If own user is edited, reload its information
    const ownUser: User | undefined = yield (select(currentUser));
    if (action.userIds.find((id) => id === ownUser?.id) !== undefined) {
        yield put(getSelfInfo());
    }
}

function* watchImportCSV() {
    yield takeEveryWithErrorHandling(ActionTypeNames.ImportCSV, importCSV);
}

function* importCSV(action: ImportCSVAction) {
    yield call(UserRepository.csvImport, action.csv, action.emailUsers, action.workspaceId);
    yield putResolve(fetchUsersCreator());
}

export default [watchFetchTableUsersNoSearch,watchFetchTableUsers, watchFetchAllUsers, watchActivateUsers, watchUpdateUsers, watchDeactivateUsers,
    watchDeleteUsers, watchSendMigrateAccountMail, watchSendMigratePasswordMail,
    watchSendNewUserMail, watchSendResetPasswordMail, watchSendVerifyEmailMail,watchSynchronizeUsers,
    watchAssignUsersApplications, watchAssignUsersGroups, watchAssignUsersWorkspace,
    watchSaveSingleUser, watchCreateSingleUser, watchFetchSingleUser, watchNewSingleUser, watchCopySingleUser,
    watchImportCSV, watchCreateApplicationKey
];